import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import {
  DepotOptimisticPlanningService,
  DepotRowStatus
} from '../../../optimistic-planning/depot-optimistic-planning.service';
import { CalculationOperation } from '../../../optimistic-planning/calculation-state.models';
import { DepotOptimisticGuardService } from '../../../optimistic-planning/depot-optimistic-guard.service';
import { DepotGuardEntry } from '../../../store/depot-optimistic-guard.models';

/**
 * Compact, space-saving inline indicator for a single depot PB row. Shows only a colored icon;
 * the full context is on the native hover tooltip (no popover — anything that overflows the Kendo
 * cell gets clipped, and disabling that clipping breaks the grid's virtualization):
 *  - pulsing dot while a plan/unplan is in flight,
 *  - green check when planned/unplanned (tooltip shows the TO; click to dismiss),
 *  - red triangle on failure (click to retry),
 *  - amber circle when the guard flagged a server discrepancy (click to dismiss),
 *  - blue pin when a just-acted-on row is kept visible (tooltip "Kept (planned) <TO>"; click to dismiss).
 */
@Component({
  selector: 'depot-row-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="dind" *ngIf="(status$ | async) as s">
      <span class="dind__ic dind__ic--info" *ngIf="s.state === 'planning' || s.state === 'unplanning'"
            [title]="t(s.state === 'unplanning' ? 'DEPOT_OPTIMISTIC.UNPLANNING' : 'DEPOT_OPTIMISTIC.PLANNING')">
        <span class="dind__dot" [class.dind__dot--unplan]="s.state === 'unplanning'"></span>
      </span>

      <span class="dind__ic dind__ic--ok dind__ic--btn" *ngIf="s.state === 'success' || s.state === 'unplanned'"
            (click)="dismiss(s.operation, $event)"
            [title]="t(successKey(s))
                     + dot(s.operation?.result?.createdTransportOrderId) + dash(t('DEPOT_OPTIMISTIC.CLICK_DISMISS'))">
        <i class="fa fa-check-circle"></i>
      </span>

      <span class="dind__ic dind__ic--err dind__ic--btn" *ngIf="s.state === 'error'"
            (click)="retry(s.operation, $event)"
            [title]="t(s.isUnplan ? 'DEPOT_OPTIMISTIC.UNPLAN_FAILED' : 'DEPOT_OPTIMISTIC.PLAN_FAILED') + dash(t('DEPOT_OPTIMISTIC.CLICK_RETRY'))">
        <i class="fa fa-exclamation-triangle"></i>
      </span>
    </span>

    <span class="dind" *ngIf="(guard$ | async) as g">
      <span class="dind__ic dind__ic--warn dind__ic--btn" *ngIf="g.state === 'flagged'"
            (click)="dismissGuard(g, $event)"
            [title]="t('DEPOT_OPTIMISTIC.SERVER_DISCREPANCY') + (g.reason ? ': ' + g.reason : '') + dash(t('DEPOT_OPTIMISTIC.CLICK_DISMISS'))">
        <i class="fa fa-exclamation-circle"></i>
      </span>

      <span class="dind__ic dind__ic--info dind__ic--btn" *ngIf="g.state !== 'flagged' && (status$ | async)?.state === 'idle'"
            (click)="dismissGuard(g, $event)"
            [title]="t(g.action === 'unplan' ? 'DEPOT_OPTIMISTIC.KEPT_UNPLAN' : 'DEPOT_OPTIMISTIC.KEPT_PLAN')
                     + dot(g.toId) + dash(t('DEPOT_OPTIMISTIC.CLICK_DISMISS'))">
        <i class="fa fa-thumbtack"></i>
      </span>
    </span>
  `,
  styleUrls: ['./depot-row-status.component.scss']
})
export class DepotRowStatusComponent implements OnInit {
  // Kendo recycles row components by position when rows are added/removed, re-feeding @Input pbId.
  // Drive everything off a subject so the indicator always reflects its CURRENT PB's data — never
  // a stale value left over from the PB this DOM row used to show.
  private readonly pbId$ = new BehaviorSubject<string | number | null>(null);
  @Input() set pbId(value: string | number) { this.pbId$.next(value); }

  public status$: Observable<DepotRowStatus>;
  public guard$: Observable<DepotGuardEntry | null>;

  constructor(
    private readonly depotOptimistic: DepotOptimisticPlanningService,
    private readonly depotGuard: DepotOptimisticGuardService,
    private readonly translate: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.status$ = combineLatest([this.pbId$, this.depotOptimistic.operations$]).pipe(
      map(([id, ops]) => this.depotOptimistic.resolveRowStatus(id, ops))
    );
    this.guard$ = this.pbId$.pipe(
      switchMap(id => this.depotGuard.guardFor(id))
    );
  }

  /** Success-chip label: a pre-plan-only operation must read "Pre-planned", not "Planned". */
  public successKey(s: DepotRowStatus): string {
    if (s.state === 'unplanned') return 'DEPOT_OPTIMISTIC.UNPLANNED';
    return s.operation?.result?.summary?.headline === 'Pre-planned'
      ? 'DEPOT_OPTIMISTIC.PREPLANNED'
      : 'DEPOT_OPTIMISTIC.PLANNED';
  }

  /** Tiny helpers to compose the native tooltip text. */
  public t(key: string): string { return this.translate.instant(key); }
  public dot(toId?: string): string { return toId ? ' · ' + toId : ''; }
  public dash(s: string): string { return ' — ' + s; }

  public dismiss(operation: CalculationOperation, event: MouseEvent): void {
    event.stopPropagation();
    this.depotOptimistic.dismiss(operation);
  }

  public retry(operation: CalculationOperation, event: MouseEvent): void {
    event.stopPropagation();
    this.depotOptimistic.retry(operation);
  }

  public dismissGuard(entry: DepotGuardEntry, event: MouseEvent): void {
    event.stopPropagation();
    this.depotGuard.dismiss(entry.pbId);
  }
}
